import { createApp } from "vue"
import { createPinia } from 'pinia'
import axios from "axios"
import App from "../App.vue"
import router from "../router/index.js"
import { frontendTabData } from "../js-modules/frontendTabData.js"
import { v4 as uuidv4 } from 'uuid';
import { useUserStore } from "../stores/userStore.js"
import { getCsrfToken, refreshCsrfToken } from "../js-modules/csrfToken.js"
import "@/stylesheets/main.scss"
import "devextreme/dist/css/dx.common.css"
import "@/stylesheets/dx.fluent.lease-analytics.css"
import "@/stylesheets/devextreme-overrides.scss"
import "devextreme/ui/form";
import "devextreme/ui/text_area";

frontendTabData.tabId =  uuidv4();
  // crypto.randomUUID?.() ??

axios.interceptors.request.use((config) => {
  config.headers['Factz-Tab-Id'] = frontendTabData.tabId
  
  // Safely get CSRF token - use utility function that handles missing meta tag
  const csrfToken = getCsrfToken()
  if (csrfToken) {
    config.headers['X-CSRF-Token'] = csrfToken
  }
  
  return config;
})

const pinia = createPinia()

const app = createApp(App)
app.use(router)
app.use(pinia)

// Track retry attempts to prevent infinite loops
const csrfRetryMap = new Map()

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle CSRF token errors (422 Unprocessable Entity with InvalidAuthenticityToken)
    if (error.response?.status === 422) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || ''
      const isCsrfError = errorMessage.includes('InvalidAuthenticityToken') || 
                         errorMessage.includes('CSRF') ||
                         errorMessage.includes('Can\'t verify CSRF token')
      
      if (isCsrfError) {
        const requestUrl = error.config?.url || ''
        const retryKey = `${requestUrl}-${JSON.stringify(error.config?.data || '')}`
        
        // Check if this is an authentication endpoint
        const authEndpoints = [
          '/api/authentication/login',
          '/api/authentication/logout',
          '/api/authentication/register',
          '/api/authentication/reset_password',
          '/api/authentication/forgot_password'
        ]
        const isAuthEndpoint = authEndpoints.some(endpoint => requestUrl.includes(endpoint))
        
        // Prevent infinite retry loops - only retry once per unique request
        if (!csrfRetryMap.has(retryKey)) {
          csrfRetryMap.set(retryKey, true)
          
          // Refresh CSRF token for all endpoints
          try {
            await refreshCsrfToken()
            const newToken = getCsrfToken()
            
            if (newToken && !isAuthEndpoint) {
              // For non-auth endpoints, retry the request automatically
              // For auth endpoints, just refresh the token - user can retry manually
              error.config.headers['X-CSRF-Token'] = newToken
              return axios.request(error.config)
            }
          } catch (refreshError) {
            console.error('Failed to refresh CSRF token:', refreshError)
          }
          
          // Clean up retry tracking after a delay
          setTimeout(() => csrfRetryMap.delete(retryKey), 5000)
        }
      }
    }
    
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Skip handling 401 errors for authentication endpoints
      const authEndpoints = [
        '/api/authentication/login',
        '/api/authentication/logout',
        '/api/authentication/register',
        '/api/authentication/reset_password',
        '/api/authentication/forgot_password'
      ]
      const requestUrl = error.config?.url || ''

      if (!authEndpoints.some(endpoint => requestUrl.includes(endpoint))) {
        const userStore = useUserStore()
        userStore.logout()
      }
    }
    
    return Promise.reject(error)
  }
)

app.mount("#app")
